import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, Play, Pause, Square, Radio, Trash2, Download } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SignalsUpload = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [signalCount, setSignalCount] = useState(0);
  
  // Fetch signals state
  const [fetchingSignals, setFetchingSignals] = useState(false);
  const [fetchAccountId, setFetchAccountId] = useState("");
  const [showFetchDialog, setShowFetchDialog] = useState(false);
  const [fetchProgress, setFetchProgress] = useState({ step: 0, message: "" });
  const [fetchingAll, setFetchingAll] = useState(false);
  const [lastFetchMeta, setLastFetchMeta] = useState(null);
  
  // Clear signals state
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearAccountId, setClearAccountId] = useState("all");
  const [clearing, setClearing] = useState(false);
  
  // Monitoring state
  const [monitoringStatus, setMonitoringStatus] = useState(null);
  const [checkInterval, setCheckInterval] = useState(60);
  const [autoStart, setAutoStart] = useState(false);
  const [loadingMonitoring, setLoadingMonitoring] = useState(false);

  useEffect(() => {
    fetchAccounts();
    fetchMonitoringStatus();
    fetchSignalCount();
    
    // Poll monitoring status every 30 seconds
    const interval = setInterval(fetchMonitoringStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSignalCount = async () => {
    try {
      const response = await api.get("/trade-signals");
      setSignalCount(response.data.length);
    } catch (error) {
      console.error("Error fetching signal count:", error);
    }
  };

  const fetchMonitoringStatus = async () => {
    try {
      const response = await api.get("/monitoring/status");
      setMonitoringStatus(response.data.data);
    } catch (error) {
      console.error("Error fetching monitoring status:", error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await api.get("/accounts");
      setAccounts(response.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      toast.error("Failed to load accounts");
    }
  };

  const handleStartMonitoring = async () => {
    setLoadingMonitoring(true);
    try {
      const response = await api.post("/monitoring/start", {
        interval_seconds: checkInterval,
        auto_start: autoStart
      });
      if (response.data.status) {
        toast.success("Signal monitoring started!");
        setMonitoringStatus(response.data.data);
      }
    } catch (error) {
      toast.error("Failed to start monitoring");
    } finally {
      setLoadingMonitoring(false);
    }
  };

  const handleStopMonitoring = async () => {
    setLoadingMonitoring(true);
    try {
      const response = await api.post("/monitoring/stop");
      if (response.data.status) {
        toast.success("Signal monitoring stopped");
        setMonitoringStatus(response.data.data);
      }
    } catch (error) {
      toast.error("Failed to stop monitoring");
    } finally {
      setLoadingMonitoring(false);
    }
  };

  const handlePauseMonitoring = async () => {
    setLoadingMonitoring(true);
    try {
      const response = await api.post("/monitoring/pause");
      if (response.data.status) {
        toast.success("Signal monitoring paused");
        setMonitoringStatus(response.data.data);
      }
    } catch (error) {
      toast.error("Failed to pause monitoring");
    } finally {
      setLoadingMonitoring(false);
    }
  };

  const handleResumeMonitoring = async () => {
    setLoadingMonitoring(true);
    try {
      const response = await api.post("/monitoring/resume");
      if (response.data.status) {
        toast.success("Signal monitoring resumed");
        setMonitoringStatus(response.data.data);
      }
    } catch (error) {
      toast.error("Failed to resume monitoring");
    } finally {
      setLoadingMonitoring(false);
    }
  };

  const handleMarketOverride = async (nseOpen, mcxOpen) => {
    try {
      const response = await api.post("/market-override", {
        nse_open: nseOpen,
        mcx_open: mcxOpen,
        reason: "Manual override - Special Session"
      });
      if (response.data.status) {
        toast.success("Market override set");
        fetchMonitoringStatus();
      }
    } catch (error) {
      toast.error("Failed to set market override");
    }
  };

  const handleClearMarketOverride = async () => {
    try {
      const response = await api.post("/market-override/clear");
      if (response.data.status) {
        toast.success("Market override cleared");
        fetchMonitoringStatus();
      }
    } catch (error) {
      toast.error("Failed to clear market override");
    }
  };

  const handleClearSignals = async () => {
    setClearing(true);
    try {
      const url = clearAccountId === "all" 
        ? "/trade-signals"
        : `/trade-signals?account_id=${clearAccountId}`;
      
      const response = await api.delete(url);
      toast.success(response.data.message);
      setShowClearDialog(false);
      setUploadResult(null);
      fetchSignalCount();
    } catch (error) {
      console.error("Error clearing signals:", error);
      toast.error(error.userMessage || "Failed to clear signals");
    } finally {
      setClearing(false);
    }
  };

  const handleFetchSignals = async () => {
    if (!fetchAccountId) {
      toast.error("Please select an account");
      return;
    }

    // Check if account has SpeedBot credentials
    const account = accounts.find(a => a.id === fetchAccountId);
    if (!account?.speedbot_credentials?.email) {
      toast.error("SpeedBot credentials not configured for this account. Please configure them in Settings > Accounts.");
      setShowFetchDialog(false);
      return;
    }

    setFetchingSignals(true);
    setFetchProgress({ step: 1, message: "Connecting to SpeedBot..." });
    
    // Use a flag to track if operation is still in progress
    let isStillFetching = true;
    
    // Set up progress timers - these simulate the backend progress
    const timer2 = setTimeout(() => { if (isStillFetching) setFetchProgress({ step: 2, message: "Logging in to SpeedBot platform..." }); }, 3000);
    const timer3 = setTimeout(() => { if (isStillFetching) setFetchProgress({ step: 3, message: "Navigating to Order Sheet..." }); }, 8000);
    const timer4 = setTimeout(() => { if (isStillFetching) setFetchProgress({ step: 4, message: "Downloading Order Sheet file..." }); }, 15000);
    const timer5 = setTimeout(() => { if (isStillFetching) setFetchProgress({ step: 5, message: "Processing signals..." }); }, 22000);
    
    const clearAllTimers = () => {
      isStillFetching = false;
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
      clearTimeout(timer5);
    };
    
    try {
      const response = await api.post(`/fetch-signals/${fetchAccountId}`, {}, {
        withCredentials: true,
        timeout: 120000 // 2 minute timeout for SpeedBot
      });
      
      // Clear progress timers
      clearAllTimers();
      setFetchProgress({ step: 6, message: "Complete!" });
      
      if (response.data.status) {
        toast.success(response.data.message);
        setUploadResult({
          signals_count: response.data.signals_count,
          new_signals: response.data.new_signals,
          updates: response.data.updates
        });
        fetchSignalCount();
      }
      setShowFetchDialog(false);
    } catch (error) {
      // Clear progress timers
      clearAllTimers();
      
      console.error("Error fetching signals:", error);
      const errorMessage = error.response?.data?.detail || "Failed to fetch signals from SpeedBot";
      toast.error(errorMessage);
    } finally {
      setFetchingSignals(false);
      setFetchProgress({ step: 0, message: "" });
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error("Please select an Excel file (.xlsx or .xls)");
        return;
      }
      setFile(selectedFile);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedAccount) {
      toast.error("Please select an account");
      return;
    }
    
    if (!file) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        `/upload-excel/${selectedAccount}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setUploadResult(response.data);
      toast.success(`Successfully processed ${response.data.signals_count} signals`);
      
      // Clear file and refresh count after successful upload
      setFile(null);
      fetchSignalCount();
      
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.xlsx') && !droppedFile.name.endsWith('.xls')) {
        toast.error("Please drop an Excel file (.xlsx or .xls)");
        return;
      }
      setFile(droppedFile);
      setUploadResult(null);
    }
  };

  return (
    <div data-testid="signals-upload-page">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="font-heading font-semibold tracking-tight text-4xl sm:text-5xl text-white mb-2">
            Upload Trade Signals
          </h1>
          <p className="text-slate-400 text-base">Upload Excel order sheet to generate trade signals and alerts</p>
        </div>
        
        {/* Signal Count & Clear Button */}
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-slate-400 text-sm">Current Signals</p>
            <p className="text-white text-2xl font-semibold">{signalCount}</p>
          </div>
          <Button
            onClick={() => setShowClearDialog(true)}
            variant="outline"
            className="border-loss/50 text-loss hover:bg-loss/10 flex items-center gap-2"
            data-testid="clear-signals-btn"
          >
            <Trash2 className="w-4 h-4" />
            Clear Signals
          </Button>
        </div>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Account Selection */}
        <div className="bg-charcoal/50 border border-white/10 rounded-md p-6">
          <Label htmlFor="account" className="text-slate-300 text-sm font-medium mb-2 block">
            Select Account <span className="text-red-400">*</span>
          </Label>
          <Select value={selectedAccount} onValueChange={setSelectedAccount}>
            <SelectTrigger
              id="account"
              data-testid="account-select"
              className="bg-black/20 border-white/10 focus:border-accent focus:ring-1 focus:ring-accent rounded-sm text-white"
            >
              <SelectValue placeholder="Select trading account" />
            </SelectTrigger>
            <SelectContent className="bg-charcoal border-white/10">
              {accounts.map((account) => (
                <SelectItem key={account.id} value={account.id}>
                  {account.name} {account.broker && `(${account.broker})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* File Upload */}
        <div
          className="bg-charcoal/50 border-2 border-dashed border-white/10 rounded-md p-8 text-center hover:border-white/20 transition-colors"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="file-upload"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="hidden"
          />
          
          <label htmlFor="file-upload" className="cursor-pointer">
            <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-slate-400" />
            <p className="text-white font-medium mb-2">
              Drop your Excel file here or click to browse
            </p>
            <p className="text-sm text-slate-400">
              Supports .xlsx and .xls files
            </p>
          </label>

          {file && (
            <div className="mt-6 p-4 bg-white/5 rounded-md flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="w-6 h-6 text-profit" />
                <span className="text-white font-mono text-sm">{file.name}</span>
              </div>
              <button
                onClick={() => setFile(null)}
                className="text-slate-400 hover:text-white transition-colors text-sm"
              >
                Remove
              </button>
            </div>
          )}
        </div>

        {/* Upload Button */}
        <div className="flex gap-4">
          <button
            onClick={handleUpload}
            disabled={uploading || !file || !selectedAccount}
            data-testid="upload-button"
            className="flex-1 bg-white text-black hover:bg-slate-200 rounded-sm font-medium px-8 py-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                Processing...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Upload & Process Signals
              </>
            )}
          </button>

          <button
            onClick={() => {
              // Pre-populate fetchAccountId with selectedAccount if available
              if (selectedAccount) {
                setFetchAccountId(selectedAccount);
              }
              setShowFetchDialog(true);
            }}
            disabled={fetchingSignals}
            data-testid="fetch-signals-button"
            className="bg-yellow-400 text-black hover:bg-yellow-500 rounded-sm font-medium px-6 py-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {fetchingSignals ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                Fetching...
              </>
            ) : (
              <>
                <FileSpreadsheet className="w-5 h-5" />
                Fetch from SpeedBot
              </>
            )}
          </button>

          <button
            onClick={async () => {
              if (fetchingAll) return;
              setFetchingAll(true);
              setUploadResult(null);
              try {
                const r = await api.post("/speedbot-api/signals/fetch-now");
                const { accounts_processed = 0, signals_created = 0, trades_updated = 0, errors = 0 } = r.data || {};
                setLastFetchMeta({
                  at: new Date().toISOString(),
                  accounts_processed,
                  signals_created,
                  trades_updated,
                  errors,
                });
                if (errors) {
                  toast.warning(`Fetched for ${accounts_processed} accounts with ${errors} error(s) — ${signals_created} signals, ${trades_updated} trades updated`);
                } else {
                  toast.success(`Fetched for ${accounts_processed} accounts — ${signals_created} signals created, ${trades_updated} trades updated`);
                }
                fetchSignalCount();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Fetch (all accounts) failed");
              } finally {
                setFetchingAll(false);
              }
            }}
            disabled={fetchingAll || fetchingSignals}
            data-testid="fetch-all-accounts-btn"
            title="Run the SpeedBot signals fetcher for every account with credentials. Same process the scheduler runs every 10 minutes during market hours."
            className="bg-violet-500 text-white hover:bg-violet-400 rounded-sm font-medium px-6 py-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {fetchingAll ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Fetching all…
              </>
            ) : (
              <>
                <FileSpreadsheet className="w-5 h-5" />
                Fetch from SpeedBot (All Accounts)
              </>
            )}
          </button>
        </div>

        {lastFetchMeta && (
          <div
            data-testid="last-fetch-meta"
            className="mt-3 text-xs text-slate-400 bg-black/20 border border-white/5 rounded px-3 py-2 font-mono"
          >
            Last fetch: <span className="text-slate-200">{new Date(lastFetchMeta.at).toLocaleString("en-IN")}</span>
            {" · "}{lastFetchMeta.accounts_processed} accounts
            {" · "}{lastFetchMeta.signals_created} signals
            {" · "}{lastFetchMeta.trades_updated} trades updated
            {lastFetchMeta.errors > 0 && <span className="text-rose-400"> · {lastFetchMeta.errors} error(s)</span>}
          </div>
        )}

        {/* Upload Result */}
        {uploadResult && (
          <div className="bg-profit/10 border border-profit/30 rounded-md p-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-profit mt-1" />
              <div>
                <h3 className="text-white font-medium mb-2">Upload Successful!</h3>
                <div className="space-y-1 text-sm text-slate-300">
                  <p>Total Signals: {uploadResult.signals_count}</p>
                  <p>New Trade Signals: {uploadResult.new_signals}</p>
                  <p>Position Updates: {uploadResult.updates}</p>
                </div>
                <p className="mt-3 text-xs text-slate-400">
                  Telegram confirmation sent
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Signal Monitoring Control Panel */}
        <div className="bg-charcoal/50 border border-white/10 rounded-md p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-heading font-semibold text-xl text-white flex items-center gap-2">
                <Radio className="w-5 h-5" />
                Signal Monitoring
              </h2>
              <p className="text-slate-400 text-sm mt-1">
                Automatically check prices and send alerts
              </p>
            </div>
            
            {/* Status Indicator */}
            {monitoringStatus && (
              <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-2 ${
                monitoringStatus.is_running && !monitoringStatus.is_paused
                  ? 'bg-profit/20 text-profit border border-profit/30'
                  : monitoringStatus.is_paused
                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
              }`}>
                <span className={`w-2 h-2 rounded-full ${
                  monitoringStatus.is_running && !monitoringStatus.is_paused
                    ? 'bg-profit animate-pulse'
                    : monitoringStatus.is_paused
                    ? 'bg-yellow-400'
                    : 'bg-slate-500'
                }`}></span>
                {monitoringStatus.is_running && !monitoringStatus.is_paused
                  ? 'Running'
                  : monitoringStatus.is_paused
                  ? 'Paused'
                  : 'Stopped'}
              </div>
            )}
          </div>

          {/* Market Status */}
          {monitoringStatus?.market_status && (
            <div className="mb-6 p-4 bg-black/20 rounded-md border border-white/5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-300">Market Status</h3>
                {monitoringStatus.market_status.override?.override_active && (
                  <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded">
                    Override Active
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-slate-500">NSE Market</p>
                  <p className={`text-sm font-medium ${monitoringStatus.market_status.nse_open ? 'text-profit' : 'text-slate-400'}`}>
                    {monitoringStatus.market_status.nse_open ? '● Open' : '○ Closed'}
                    {monitoringStatus.market_status.override?.nse_force_open && 
                      <span className="text-yellow-400 ml-1">(Forced)</span>
                    }
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">MCX Market</p>
                  <p className={`text-sm font-medium ${monitoringStatus.market_status.mcx_open ? 'text-profit' : 'text-slate-400'}`}>
                    {monitoringStatus.market_status.mcx_open ? '● Open' : '○ Closed'}
                    {monitoringStatus.market_status.override?.mcx_force_open && 
                      <span className="text-yellow-400 ml-1">(Forced)</span>
                    }
                  </p>
                </div>
              </div>
              
              <div className="text-xs text-slate-500 mb-4">
                Current Time (IST): {monitoringStatus.market_status.current_time_ist} | {monitoringStatus.market_status.weekday}
              </div>
              
              {/* Market Override Controls */}
              <div className="border-t border-white/10 pt-4">
                <p className="text-xs text-slate-400 mb-3">
                  Override for special sessions (Budget Day, Extended Hours, etc.)
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => handleMarketOverride(true, null)}
                    size="sm"
                    variant="outline"
                    className={`text-xs ${monitoringStatus.market_status.override?.nse_force_open ? 'bg-profit/20 border-profit text-profit' : 'border-white/20 text-slate-300'}`}
                  >
                    Force NSE Open
                  </Button>
                  <Button
                    onClick={() => handleMarketOverride(null, true)}
                    size="sm"
                    variant="outline"
                    className={`text-xs ${monitoringStatus.market_status.override?.mcx_force_open ? 'bg-profit/20 border-profit text-profit' : 'border-white/20 text-slate-300'}`}
                  >
                    Force MCX Open
                  </Button>
                  <Button
                    onClick={() => handleMarketOverride(true, true)}
                    size="sm"
                    variant="outline"
                    className={`text-xs ${monitoringStatus.market_status.override?.nse_force_open && monitoringStatus.market_status.override?.mcx_force_open ? 'bg-profit/20 border-profit text-profit' : 'border-white/20 text-slate-300'}`}
                  >
                    Force Both Open
                  </Button>
                  {monitoringStatus.market_status.override?.override_active && (
                    <Button
                      onClick={handleClearMarketOverride}
                      size="sm"
                      variant="outline"
                      className="text-xs border-loss/50 text-loss hover:bg-loss/10"
                    >
                      Clear Override
                    </Button>
                  )}
                </div>
                {monitoringStatus.market_status.override?.override_reason && (
                  <p className="text-xs text-yellow-400 mt-2">
                    Reason: {monitoringStatus.market_status.override.override_reason}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Configuration */}
          <div className="space-y-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label className="text-slate-300 text-sm">Check Interval</Label>
                <Select 
                  value={checkInterval.toString()} 
                  onValueChange={(val) => setCheckInterval(parseInt(val))}
                  disabled={monitoringStatus?.is_running}
                >
                  <SelectTrigger className="bg-black/20 border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    <SelectItem value="10">Every 10 seconds (Fast)</SelectItem>
                    <SelectItem value="15">Every 15 seconds</SelectItem>
                    <SelectItem value="30">Every 30 seconds</SelectItem>
                    <SelectItem value="60">Every 1 minute</SelectItem>
                    <SelectItem value="120">Every 2 minutes</SelectItem>
                    <SelectItem value="300">Every 5 minutes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch 
                  checked={autoStart} 
                  onCheckedChange={setAutoStart}
                  disabled={monitoringStatus?.is_running}
                />
                <Label className="text-slate-300 text-sm">Auto-start on server restart</Label>
              </div>
            </div>
          </div>

          {/* Control Buttons */}
          <div className="flex gap-3">
            {!monitoringStatus?.is_running ? (
              <Button
                onClick={handleStartMonitoring}
                disabled={loadingMonitoring}
                data-testid="start-monitoring-btn"
                className="bg-profit text-white hover:bg-profit/80 flex items-center gap-2"
              >
                <Play className="w-4 h-4" />
                Start Monitoring
              </Button>
            ) : (
              <>
                {monitoringStatus.is_paused ? (
                  <Button
                    onClick={handleResumeMonitoring}
                    disabled={loadingMonitoring}
                    data-testid="resume-monitoring-btn"
                    className="bg-profit text-white hover:bg-profit/80 flex items-center gap-2"
                  >
                    <Play className="w-4 h-4" />
                    Resume
                  </Button>
                ) : (
                  <Button
                    onClick={handlePauseMonitoring}
                    disabled={loadingMonitoring}
                    data-testid="pause-monitoring-btn"
                    className="bg-yellow-500 text-black hover:bg-yellow-400 flex items-center gap-2"
                  >
                    <Pause className="w-4 h-4" />
                    Pause
                  </Button>
                )}
                <Button
                  onClick={handleStopMonitoring}
                  disabled={loadingMonitoring}
                  data-testid="stop-monitoring-btn"
                  variant="outline"
                  className="border-loss text-loss hover:bg-loss/10 flex items-center gap-2"
                >
                  <Square className="w-4 h-4" />
                  Stop
                </Button>
              </>
            )}
          </div>

          {/* Last Check Info */}
          {monitoringStatus?.last_check && (
            <p className="mt-4 text-xs text-slate-500">
              Last check: {new Date(monitoringStatus.last_check).toLocaleString()}
            </p>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-blue-900/20 border border-blue-900/50 rounded-md p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
            <div className="text-sm text-slate-300 space-y-2">
              <p className="font-medium text-white">Excel Format Expected:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Column B: Symbol</li>
                <li>Column C: Trading System/Strategy</li>
                <li>Column D: Current Trade (NEW/LONG/SHORT)</li>
                <li>Column F: Monthly Trend</li>
                <li>Columns G-I: Stop Loss details</li>
                <li>Columns J-M: New entry prices</li>
                <li>Column N: Target price</li>
              </ul>
              <p className="text-xs text-slate-400 mt-3">
                System will automatically monitor prices and send <strong className="text-blue-300">Telegram alerts</strong> when Stop Loss, Target, or Entry levels are reached.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Clear Signals Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent className="bg-charcoal border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold flex items-center gap-2 text-loss">
              <Trash2 className="w-5 h-5" />
              Clear Trade Signals
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This will permanently delete signals. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="text-slate-300 text-sm">Clear signals for:</Label>
            <Select value={clearAccountId} onValueChange={setClearAccountId}>
              <SelectTrigger className="bg-black/20 border-white/10 text-white mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-charcoal border-white/10">
                <SelectItem value="all">All Accounts ({signalCount} signals)</SelectItem>
                {accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <p className="mt-4 text-sm text-yellow-400 bg-yellow-400/10 p-3 rounded-md">
              ⚠️ After clearing, you can upload fresh signals for each account by selecting the account and uploading the Excel file.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowClearDialog(false)}
              className="border-white/10 text-slate-300 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              onClick={handleClearSignals}
              disabled={clearing}
              className="bg-loss hover:bg-loss/80 text-white"
              data-testid="confirm-clear-signals-btn"
            >
              {clearing ? "Clearing..." : "Clear Signals"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fetch Signals Dialog */}
      <Dialog open={showFetchDialog} onOpenChange={setShowFetchDialog}>
        <DialogContent className="bg-charcoal border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <span className="text-yellow-400">⚡</span>
              Fetch Signals from SpeedBot
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Download Order Sheet from SpeedBot and replace all existing signals for the selected account.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Progress indicator when fetching */}
            {fetchingSignals && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-md p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-400 border-t-transparent"></div>
                  <span className="text-blue-400 font-medium">Fetching signals from SpeedBot...</span>
                </div>
                
                {/* Progress Steps */}
                <div className="space-y-2 text-sm">
                  {[
                    { step: 1, label: "Connecting to SpeedBot" },
                    { step: 2, label: "Logging in" },
                    { step: 3, label: "Navigating to Order Sheet" },
                    { step: 4, label: "Downloading file" },
                    { step: 5, label: "Processing signals" },
                  ].map(({ step, label }) => (
                    <div key={step} className="flex items-center gap-2">
                      {fetchProgress.step > step ? (
                        <CheckCircle className="w-4 h-4 text-profit" />
                      ) : fetchProgress.step === step ? (
                        <div className="w-4 h-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin"></div>
                      ) : (
                        <div className="w-4 h-4 rounded-full border border-slate-600"></div>
                      )}
                      <span className={fetchProgress.step >= step ? "text-white" : "text-slate-500"}>
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
                
                <p className="text-slate-400 text-xs mt-3">
                  This may take 30-60 seconds. Please don't close this window.
                </p>
              </div>
            )}
            
            {!fetchingSignals && (
              <>
                <div>
                  <Label className="text-slate-300 text-sm mb-2 block">
                    Select Account <span className="text-red-400">*</span>
                  </Label>
                  <Select value={fetchAccountId} onValueChange={setFetchAccountId}>
                    <SelectTrigger className="bg-black/20 border-white/10 text-white">
                      <SelectValue placeholder="Select account to fetch signals for" />
                    </SelectTrigger>
                    <SelectContent className="bg-charcoal border-white/10">
                      {accounts.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          <div className="flex items-center gap-2">
                            {account.name}
                            {account.speedbot_credentials?.email ? (
                              <span className="text-xs text-profit">✓ SpeedBot configured</span>
                            ) : (
                              <span className="text-xs text-yellow-400">⚠ No SpeedBot credentials</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-md p-3">
                  <p className="text-yellow-400 text-sm">
                    <strong>Note:</strong> This will delete ALL existing signals for this account and replace them with the new ones from SpeedBot.
                  </p>
                </div>

                {fetchAccountId && !accounts.find(a => a.id === fetchAccountId)?.speedbot_credentials?.email && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-md p-3">
                    <p className="text-red-400 text-sm">
                      SpeedBot credentials not configured for this account. 
                      <button 
                        onClick={() => { setShowFetchDialog(false); navigate('/accounts'); }}
                        className="underline ml-1 hover:text-red-300"
                      >
                        Configure in Accounts
                      </button>
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowFetchDialog(false)}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleFetchSignals}
              disabled={fetchingSignals || !fetchAccountId || !accounts.find(a => a.id === fetchAccountId)?.speedbot_credentials?.email}
              className="bg-yellow-400 text-black hover:bg-yellow-500"
              data-testid="confirm-fetch-signals-btn"
            >
              {fetchingSignals ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                  Fetching...
                </>
              ) : (
                "Fetch Signals"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SignalsUpload;
